import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getEmailVerify } from "@/services/auth.service";
import { IoClose } from "react-icons/io5";

const COOLDOWN_SECONDS = 30;

const EmailVerificationBanner = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { pathname } = useLocation();

  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const intervalRef = useRef(null);

  const isCurriculum = pathname.includes("curriculum");

  // When banner is visible, push the fixed topbar, sidenav, and content down
  useEffect(() => {
    const styleId = "email-banner-topbar-offset";
    let styleEl = document.getElementById(styleId);

    const emailVerifiedAt = user?.email_verified_at;
    const bannerVisible = user && emailVerifiedAt === null && !isCurriculum && !dismissed;

    if (bannerVisible) {
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.innerHTML = `
        .topBar { top: 40px !important; }
        .sideNavFixed { top: 104px !important; }
        .currSideNavFixed { top: 104px !important; }
        #content { margin-top: 40px !important; }
      `;
    } else {
      if (styleEl) {
        styleEl.innerHTML = `
          .topBar { top: 0 !important; }
          .sideNavFixed { top: 64px !important; }
          .currSideNavFixed { top: 64px !important; }
          #content { margin-top: 0 !important; }
        `;
      }
    }

    return () => {
      const el = document.getElementById(styleId);
      if (el) {
        el.innerHTML = `
          .topBar { top: 0 !important; }
          .sideNavFixed { top: 64px !important; }
          .currSideNavFixed { top: 64px !important; }
          #content { margin-top: 0 !important; }
        `;
      }
    };
  }, [user, isCurriculum, dismissed]);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const result = await dispatch(getEmailVerify()).unwrap();
      if (result?.success) {
        toast.success(result?.message || "Verification email sent successfully.");
        setDismissed(true);
      } else {
        toast.error(result?.message || "Failed to send verification email.");
      }
    } catch (err) {
      toast.error(
        err?.message || "Failed to send verification email. Please try again."
      );
    } finally {
      setLoading(false);
      startCooldown();
    }
  };

  // Show banner only when email_verified_at is explicitly null (unverified).
  // Treat undefined (user loaded from login token before profile API resolves) as
  // "not yet known" — wait until the profile API populates the field.
  const emailVerifiedAt = user?.email_verified_at;
  const isUnverified = emailVerifiedAt === null;

  if (!user || !isUnverified || isCurriculum || dismissed) {
    return null;
  }

  const isButtonDisabled = loading || cooldown > 0;
  const buttonLabel = loading
    ? "Sending..."
    : cooldown > 0
      ? `Resend in ${cooldown}s`
      : "Resend Verification Email";

  return (
    <div
      id="email-verification-banner"
      className="w-100 py-2 px-3 d-flex align-items-center justify-content-center gap-3 flex-wrap"
      style={{
        backgroundColor: "#334155",
        color: "#fff",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 600,
      }}
      role="alert"
      aria-live="polite"
    >
      <span className="fw-medium">
        Please verify your email address.
      </span>
      <button
        className="btn btn-sm btn-outline-light"
        style={{
          color: "black",
          backgroundColor: "white",
        }}
        onClick={handleResend}
        disabled={isButtonDisabled}
        aria-disabled={isButtonDisabled}
      >
        {buttonLabel}
      </button>
      <button
        className="btn btn-sm p-0 ms-2"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss email verification banner"
        style={{
          position: "absolute",
          right: "12px",
          background: "transparent",
          border: "none",
          color: "#fff",
          lineHeight: 1,
        }}
      >
        <IoClose size={20} />
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
